'use client';

import { Asset } from '@/@shared/components/primitives/Asset';
import AuthInput from '@/@shared/components/forms/AuthInput';
import SubmitButton from '@/@shared/components/primitives/Button/SubmitButton';
import { Spacer } from '@/@shared/components/primitives/Spacer/Spacer';
import { useTransAuth } from '@/@shared/hooks/useTranslations';
import { useLoginForm } from '@/app/[locale]/(auth)/login/hooks/useLoginForm';
import styles from '@/app/[locale]/(auth)/login/LoginForm.module.scss';
import SocialBtn from '@/legacy/components/btn/SocialBtn';
import { errorStyle } from '@/legacy/components/input/inputUtil';
import { SIGNUP_PATHNAME } from '@/legacy/constants/pathname';
import { useGetUser } from '@/model/dexie/userDexie';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

type Props = {
	locale: string;
};

const LoginForm = ({ locale }: Props) => {
	const t = useTransAuth('login.loginForm');

	const socialText = ['Apple', 'Google'] as const;

	const { methods, submitLogin, isSubmitting } = useLoginForm(locale);

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors, isValid },
	} = methods;

	const emailRegister = register('email');
	const passwordRegister = register('password');

	// Enter 키로 폼 제출
	const handleEnterPress = () => {
		if (isValid && !isSubmitting) {
			handleSubmit(submitLogin)();
		}
	};

	const router = useRouter();
	const userData = useGetUser();

	// 이미 로그인된 사용자는 홈으로 리다이렉트
	useEffect(() => {
		if (userData && userData.id) {
			console.log('[LoginForm] 이미 로그인된 사용자, 홈으로 리다이렉트');
			router.push(`/${locale}`);
			return;
		}
	}, [userData, router, locale]);

	useEffect(() => {
		window.scrollTo({ behavior: 'auto', left: 0, top: 0 });
	}, []);

	return (
		<div className={styles.area}>
			<div className={styles.space}>
				<div className={styles.wrap}>
					<div className={styles.title}>
						<Asset type="icon" name="auth-icon-stepin-logo-png" size={{ height: 80 }} alt="STEPIN Tournament" />

						<Spacer size={8} />
						<div className={styles.line1}>{t('title')}</div>
						<Spacer size={8} />
						<div className={styles.line2}>
							<span>{t('subtitle')}</span>
							<Link className={styles.link} href={SIGNUP_PATHNAME(locale)} aria-label="Go to sign up page">
								{t('signupLink')}
							</Link>
						</div>
					</div>

					<Spacer size={40} />

					<div className={styles.frame}>
						<FormProvider {...methods}>
							<form onSubmit={handleSubmit(submitLogin)}>
								<AuthInput
									className={errorStyle(errors.email?.message?.toString())}
									errors={errors}
									keyName="email"
									required
									{...emailRegister}
									autoComplete="username"
									getValues={getValues}
									placeholder={t('emailLabel')}
									setValue={setValue}
									type="email"
									onEnterPress={handleEnterPress}
								/>
								<Spacer size={16} />
								<AuthInput
									className={errorStyle(errors.password?.message?.toString())}
									errors={errors}
									keyName="password"
									required
									{...passwordRegister}
									autoComplete="current-password"
									getValues={getValues}
									placeholder={t('passwordLabel')}
									setValue={setValue}
									type="password"
									onEnterPress={handleEnterPress}
								/>

								<Spacer size={40} />

								<SubmitButton
									loading={isSubmitting}
									isValid={isValid}
									action={t('submit')}
									loadingText={t('loading')}
									onClick={() => {}}
									type="submit"
									fullWidth
								/>

								<Spacer size={16} />

								<div className={styles.frame}>
									<span className={styles.sns}>{t('snsDescription')}</span>
									<div className={styles.socials}>
										{socialText.map((social) => (
											<SocialBtn authType={'login'} key={social} social={social} />
										))}
									</div>
								</div>
							</form>
						</FormProvider>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LoginForm;
